import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CloseIcon, LawIcon } from "./Icons.tsx";
import { SidebarItem } from "./SidebarItem.tsx";
import type { ChatSession } from "../../types";

/** Extends the shared ChatSession with a title derived from the first user message. */
interface SidebarSession extends ChatSession {
  title: string;
}

interface SidebarProps {
  currentSessionId: string | null;
  onSelectSession: (id: string | null) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ currentSessionId, onSelectSession, isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<SidebarSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState(0);
  const lastPrefetchId = React.useRef<string | null>(null);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/sessions");
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
        setLastFetched(Date.now());
      }
    } catch (e) {
      console.error("Failed to load sessions", e);
    } finally {
      setLoading(false);
    }
  };

  // Reload list when the sidebar opens and data is stale (>30s old),
  // OR when currentSessionId changes to a value not yet in the list.
  useEffect(() => {
    const STALE_MS = 30_000;
    const isNewSession = currentSessionId !== null && !sessions.some(s => s.id === currentSessionId);

    // Safety check: don't loop if we already tried to fetch for this new session ID
    const hasAlreadyPrefetched = isNewSession && lastPrefetchId.current === currentSessionId;

    if (isOpen && (Date.now() - lastFetched > STALE_MS || (isNewSession && !hasAlreadyPrefetched))) {
      if (isNewSession) {
        lastPrefetchId.current = currentSessionId;
      }
      loadSessions();
    }
  }, [isOpen, currentSessionId, sessions, lastFetched]);

  const handleDeleteSession = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    try {
      const res = await fetch(`/api/sessions?sessionId=${id}`, { method: "DELETE" });
      if (res.ok) {
        setSessions(prev => prev.filter(s => s.id !== id));
        if (currentSessionId === id) {
          onSelectSession(null);
        }
      }
    } catch (e) {
      console.error("Failed to delete session", e);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="w-80 h-full border-r border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 flex flex-col shrink-0 transition-all z-40">
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition-colors text-slate-500 touch-manipulation"
              aria-label={t("common.close_sidebar", "Sulge külgriba")}
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
          <div className="hidden md:flex items-center gap-2 px-1 text-blue-600 dark:text-blue-400">
            <LawIcon />
            <span className="font-bold text-sm tracking-tight uppercase">{t("common.history", "Ajalugu")}</span>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-xl transition-colors text-slate-500 hidden md:flex touch-manipulation"
          title={t("common.close_sidebar", "Sulge külgriba")}
          aria-label={t("common.close_sidebar", "Sulge külgriba")}
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="px-4 mb-4">
        <button
          onClick={() => onSelectSession(null)}
          className="w-full flex items-center justify-center gap-2 py-3 bg-white dark:bg-slate-800 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-all group active:scale-[0.98]"
        >
          <svg className="w-5 h-5 group-hover:rotate-90 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t("common.new_chat", "Uus vestlus")}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 space-y-1 pb-4">
        {loading && (
          <div className="flex flex-col gap-2 p-3">
            {[1, 2, 3].map(i => <div key={i} className="h-10 skeleton w-full rounded-xl opacity-50" />)}
          </div>
        )}

        {!loading && sessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="w-12 h-12 bg-slate-200 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4 text-slate-400">
              <LawIcon />
            </div>
            <p className="text-sm font-medium text-slate-900 dark:text-slate-200">{t("common.no_history", "Ajalugu puudub")}</p>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{t("common.no_history_desc", "Teie juriidilised konsultatsioonid ilmuvad siia.")}</p>
          </div>
        )}

        {sessions.map(session => (
          <SidebarItem
            key={session.id}
            id={session.id}
            title={session.title}
            isActive={currentSessionId === session.id}
            onSelect={onSelectSession}
            onDelete={handleDeleteSession}
          />
        ))}
      </div>

    </div>
  );
};
